import React, { useEffect, useRef, useState } from "react";
import { FEATURE_ANIM_MAP } from "./FeatureAnimations";
import { useT } from "../context/LanguageContext";

const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
};

const ProductSection = () => {
  const { ref, visible } = useReveal();
  const t = useT();
  const productFeatures = [
    { icon: "Mic", title: t("product.feature1.title"), description: t("product.feature1.description") },
    { icon: "Phone", title: t("product.feature2.title"), description: t("product.feature2.description") },
    { icon: "Languages", title: t("product.feature3.title"), description: t("product.feature3.description") },
    { icon: "Workflow", title: t("product.feature4.title"), description: t("product.feature4.description") },
    { icon: "Activity", title: t("product.feature5.title"), description: t("product.feature5.description") },
    { icon: "FileText", title: t("product.feature6.title"), description: t("product.feature6.description") },
  ];
  return (
    <section
      id="product"
      ref={ref}
      className={`section-fade ${visible ? "visible" : ""} py-20 px-6`}
      style={{ minHeight: "100vh", background: "var(--section-surface-1)" }}
    >
      <div className="max-w-6xl mx-auto flex flex-col justify-center" style={{ minHeight: "calc(100vh - 10rem)" }}>
        <div className="flex flex-col items-center text-center mb-12">
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
            style={{
              background: "linear-gradient(135deg, rgba(75,111,212,0.12), rgba(255,122,122,0.09))",
              border: "1px solid rgba(75,111,212,0.2)",
              color: "var(--brand-cyan)",
            }}
          >
            {t("product.eyebrow")}
          </span>
          <h2
            className="font-bold tracking-tight"
            style={{
              color: "var(--fg)",
              fontSize: "clamp(2rem, 4.2vw, 3.25rem)",
            }}
          >
            {t("product.title1")}{" "}
            <span className="brand-gradient-text">{t("product.title2")}</span>
            {t("product.title3")}
          </h2>
          <p
            className="mt-4 max-w-2xl text-[15px]"
            style={{ color: "var(--fg)" }}
          >
            {t("product.subtitle")}
          </p>

          <span
            className="mt-7 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[12.5px] font-semibold"
            style={{
              background:
                "linear-gradient(90deg, rgba(75,111,212,0.16), rgba(255,122,122,0.14), rgba(20,184,166,0.14))",
              color: "var(--fg)",
              border: "1px solid rgba(75,111,212,0.2)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: "var(--brand-cyan)",
                animation: "pulseDot 1.5s ease-in-out infinite",
              }}
            />
            <span style={{ color: "var(--brand-cyan)" }}>50+</span>
            {t("product.badge")}
          </span>
        </div>

        {/* Product showcase image */}
        <div className="mb-10 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
          <img
            src="/images/pages/product-hero.jpg"
            alt="Call center team"
            className="w-full h-auto object-cover"
            style={{ maxHeight: "360px", display: "block" }}
          />
        </div>

        {/* 6 feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {productFeatures.map((f, i) => {
            const Anim = FEATURE_ANIM_MAP[f.icon];
            const accents = ["#4B6FD4", "#2F7BE8", "#4F6BD7", "#5B6FEA", "#3E67C9", "#4268D4"];
            const accentAlts = ["#7AA7FF", "#5EC8FF", "#81A2FF", "#79C9FF", "#6DB7FF", "#8AA9FF"];
            const accent = accents[i % accents.length];
            const accentAlt = accentAlts[i % accentAlts.length];
            return (
              <div
                key={f.title}
                className="group p-7 rounded-2xl transition-colors duration-200 overflow-hidden neon-box"
                style={{
                  background: "var(--card-bg)",
                  border: "1px solid var(--border)",
                }}
              >
                {Anim ? <Anim accent={accent} accentAlt={accentAlt} /> : null}
                <h3
                  className="text-[17px] font-semibold mb-2"
                  style={{ color: accent }}
                >
                  {f.title}
                </h3>
                <p
                  className="text-[14px] leading-relaxed"
                  style={{ color: "var(--fg)", opacity: 0.9 }}
                >
                  {f.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProductSection;
